import { Component, OnInit, OnDestroy, ViewChild, TemplateRef } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { StudiesService } from '../../../services/studies.service';
import { AlertService } from '../../../services/alert.service';
import { ReportService } from '../../../services/report.service';

@Component({
  selector: 'app-report-viewer',
  templateUrl: './report-viewer.component.html',
  styleUrls: ['./report-viewer.component.css']
})
export class ReportViewerComponent implements OnInit, OnDestroy {
  // Propiedades principales
  studyUID: string | null = null;  // UID del estudio para buscar informes
  documents: any[] = [];  // Lista de informes del estudio
  documentIndex: number = 0;
  selectedDocument: number | null = null;
  
  // URL del informe actual a mostrar
  reportURL: string | SafeUrl | null = null;
  
  // Control de zoom del PDF
  pdfZoom: number = 1.5;
  
  // Modal
  modal: NgbModalRef | null = null;
  @ViewChild('PDFModal', { static: false }) modalTemplate!: TemplateRef<any>;

  // Getters
  get document() { 
    return this.documents[this.documentIndex]; 
  }

  get isDocumentListEmpty() {
    return !this.documents || this.documents.length === 0;
  }

  get hasDocumentListMoreThanOne(){
    return !this.isDocumentListEmpty && this.documents.length > 1;
  }

  hasPrev(): boolean {
    return this.documentIndex > 0;
  }

  hasNext(): boolean {
    return this.documentIndex < (this.documents.length - 1);
  }

  constructor(
    private modalService: NgbModal,
    private alertService: AlertService,
    private reportService: ReportService
  ) {}

  ngOnInit(): void {
    // Inicialización si es necesaria
  }

  ngOnDestroy(): void {
    this.clearVariables();
  }

  /**
   * Método público para abrir el modal con el studyUID
   * @param studyUID - UID del estudio del cual cargar informes
   */
  showByStudyUID(studyUID: string) {
    this.studyUID = studyUID;
    this.loadReportsByStudyUID(studyUID);
  }

  /**
   * Carga los informes asociados a un studyUID desde la base de datos
   */
  private loadReportsByStudyUID(studyUID: string) {
    console.log('Cargando informes para studyUID:', studyUID);

    this.reportService.getReportsSeparately(studyUID).then(
      (bytesList) => {
        if (!bytesList || bytesList.length === 0) {
          this.alertService.alert('Este estudio no dispone de informe. Póngase en contacto con el centro para más información');
          return;
        }

        bytesList.forEach((bytes: ArrayBuffer, index: number) => {
          this.documents.push({
            studyUID: studyUID,
            bytes: bytes,
            fileName: `informe_${studyUID}_${index + 1}.pdf`,
            date: new Date()
          });
        });

        this.documentIndex = 0;
        this.showModal();
      },
      (error: any) => {
        console.error('Error cargando informes:', error);
        this.alertService.alert('Error al cargar los informes del estudio. Por favor, inténtelo de nuevo más tarde.');
      }
    );
  }

  /**
   * Abre el modal y muestra el primer informe
   */
  private showModal() {
    this.modal = this.modalService.open(this.modalTemplate, {
      size: 'xl',
      centered: true,
      scrollable: true,
      windowClass: 'report-viewer-modal',
      backdrop: 'static',
      beforeDismiss: () => { 
        this.clearVariables(); 
        return true; 
      }
    });

    if (this.document) {
      this.showDocument();
    }
  }

  /**
   * Muestra el documento actual cargando su contenido
   */
  private showDocument() {
    // Limpiar URL anterior para evitar memory leaks
    if (this.reportURL && typeof this.reportURL === 'string' && this.reportURL.startsWith('blob:')) {
      URL.revokeObjectURL(this.reportURL);
    }
    
    this.reportURL = null;
    this.selectedDocument = this.documentIndex;

    if (!this.document) {
      console.error('No hay documento para mostrar');
      return;
    }

    // Cargar el informe desde los bytes
    if (this.document.bytes) {
      this.loadReportByBytes(this.document.bytes);
    } else {
      console.error('No hay bytes del documento para mostrar');
      this.alertService.alert('Error al cargar el informe. No se encontraron datos del documento.');
    }
  }

  /**
   * Carga un informe desde un path (no implementado actualmente)
   */
  private loadReportByPath(path: string) {
    // TODO: Implementar si es necesario en el futuro
    console.warn('loadReportByPath no está implementado');
  }

  /**
   * Carga un informe desde bytes
   */
  private loadReportByBytes(bytes: ArrayBuffer) {
    const blob = new Blob([bytes], { type: 'application/pdf' });
    this.reportURL = URL.createObjectURL(blob);
  }

  /**
   * Cambia el documento mostrado cuando se selecciona otro del dropdown
   */
  changeDocument(docIndex: number) {
    
    // Limpiar URL anterior
    if (this.reportURL && typeof this.reportURL === 'string' && this.reportURL.startsWith('blob:')) {
      URL.revokeObjectURL(this.reportURL);
    }
    
    this.documentIndex = docIndex;
    this.showDocument();
  }

  /**
   * Imprime el PDF actual
   */
  print() {
    if (!this.reportURL) {
      console.warn('No hay informe para imprimir');
      return;
    }

    const iframe = document.createElement('iframe');
    document.body.appendChild(iframe);
    iframe.style.display = 'none';
    
    iframe.onload = function () {
      setTimeout(function () {
        iframe.focus();
        iframe.contentWindow?.print();
      }, 1);
    };

    iframe.src = this.reportURL as string;
  }

  /**
   * Descarga el PDF actual
   */
  download() {
    if (!this.reportURL) {
      console.warn('No hay informe para descargar');
      return;
    }

    const fileName = this.document?.fileName || 'informe.pdf';
    
    fetch(this.reportURL as string)
      .then(res => res.blob())
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(link.href);
      })
      .catch(err => {
        console.error('Error descargando el PDF:', err);
        alert('Error al descargar el documento');
      });
  }

  /**
   * Limpia todas las variables y URLs para evitar memory leaks
   */
  clearVariables() {
    // Limpiar zoom
    this.pdfZoom = 1.5;
    this.documentIndex = 0;
    
    // Revocar URLs para evitar memory leaks
    if (this.reportURL && typeof this.reportURL === 'string' && this.reportURL.startsWith('blob:')) {
      URL.revokeObjectURL(this.reportURL);
    }
    
    // Limpiar referencias
    this.reportURL = null;
    this.documents = [];
    this.selectedDocument = null;
    this.studyUID = null;
  }
}
